<template>
  <Drawer
    :id="drawerId"
    :title="drawerTitle"
    :loading="loading || !jobId"
  >
    <template
      v-if="!loading"
      #headerAppend
    >
      <div class="d-flex align-center">
        <v-img
          :src="job.providerLogoUrl"
          :height="32"
          :max-width="100"
          contain
        />
        <v-btn
          outlined
          small
          icon
          class="ml-3"
          min-width="20"
          :loading="loading"
          @click="loadData"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
      </div>
    </template>
    <div
      v-if="!loading && job.jobId"
      :class="{ 'px-4': !$vuetify.breakpoint.mobile }"
    >
      <v-row>
        <v-col cols="12">
          <v-simple-table>
            <template #default>
              <tbody>
                <tr>
                  <td>Order #</td>
                  <td>
                    <router-link :to="`/orders/${order.id}`">
                      {{ order.id }}
                    </router-link>
                    |
                    <router-link :to="`/users/${job.userInfoId}`">
                      {{ job.username }}
                    </router-link>
                  </td>
                </tr>
                <tr>
                  <td>Date</td>
                  <td>{{ order.orderDate | normalDateWithTime }}</td>
                </tr>
                <tr>
                  <td>Charge Id</td>
                  <td>{{ order.stripeChargeId }}</td>
                </tr>
                <tr>
                  <td>Total</td>
                  <td>
                    {{ job.price | currency }}
                    <span v-if="order.jobs.length > 1">
                      <span class="mx-2">|</span>
                      Order ({{ order.jobs.length }} jobs):
                      {{ order.totalPrice | currency }}
                    </span>
                  </td>
                </tr>
                <tr v-if="job.address">
                  <td>Shipping to</td>
                  <td
                    class="hover-pointer d-flex align-center"
                    @click="showEditAddress(job)"
                  >
                    <span>
                      {{ job.address.city }}, {{ job.address.state }}
                      {{ job.address.postalCode }}
                    </span>
                    <v-icon
                      size="20"
                      class="ml-2"
                      :title="
                        job.address.validated ? 'Successfully validated' : 'Validation failed'
                      "
                      :color="job.address.validated ? 'success' : 'error'"
                    >
                      <template v-if="job.address.validated"> mdi-check </template>
                      <template v-else> mdi-alert-circle-outline </template>
                    </v-icon>
                  </td>
                </tr>

                <tr>
                  <td
                    colspan="2"
                    class="text-center black white--text"
                  >
                    ------ Prepare ------
                  </td>
                </tr>
                <tr v-if="job.externalJobId">
                  <td>{{ job.provider }} Id</td>
                  <td>{{ job.externalJobId }}</td>
                </tr>
                <tr v-if="job.trackingNumber">
                  <td>Tracking #</td>
                  <td>
                    <a
                      :href="job.trackingUrl"
                      target="_blank"
                    >
                      {{ job.trackingNumber }}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td
                    colspan="2"
                    class="py-2"
                  >
                    <v-btn
                      v-if="job.providerId == $enums.PROVIDER.FOUROVER"
                      small
                      outlined
                      class="mr-2"
                      @click="showJobValidate(job)"
                    >
                      Validate
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      class="mr-2"
                      @click="showAddExternalJob(job)"
                    >
                      + Order
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      class="mr-2"
                      @click="showAddTracking(job)"
                    >
                      + Tracking
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="error"
                      target="_blank"
                      @click="prepareReprint(job)"
                    >
                      Reprint
                    </v-btn>
                  </td>
                </tr>
                <tr>
                  <td>Editor</td>
                  <td>
                    <div class="my-2">
                      <v-btn
                        v-if="job.snapshotProject"
                        block
                        large
                        :href="job.editorUrl"
                        target="_blank"
                        elevation="0"
                        :color="job.snapshotProject.useFreedomEditor ? 'mcsprimary' : 'error'"
                      >
                        Edit Artwork
                        <span
                          v-if="!job.snapshotProject.useFreedomEditor"
                          class="ml-2"
                          >(CHILI)</span
                        >
                      </v-btn>
                      <div
                        v-if="pageSelections.length"
                        class="d-flex align-center mt-4"
                      >
                        <v-select
                          v-for="(page, index) in pageSelections"
                          :key="index"
                          :value="page.pageId"
                          return-object
                          outlined
                          :label="'Print on Page ' + page.number"
                          :items="pageOptions"
                          hide-details
                          item-text="name"
                          item-value="pageId"
                          style="width: 50%"
                          :class="{
                            'mr-2': index % 2 === 0,
                            'ml-2': index % 2 === 1
                          }"
                          :menu-props="{
                            maxHeight: '100%',
                            'offset-x': true,
                            'nudge-left': '150px',
                            'offset-y': true
                          }"
                          @input="pageSelected($event, page)"
                        >
                          <template #[`selection`]="{ item }">
                            <div
                              v-if="item.pageId === 'blank'"
                              class="body-1 font-weight-bold"
                            >
                              Blank
                            </div>
                            <img
                              v-else
                              :src="item.previewUrl"
                              class="page-preview"
                            />
                          </template>
                          <template #[`item`]="{ active, item, attrs, on }">
                            <div
                              v-bind="attrs"
                              class="d-flex align-center justify-center pa-2 page-select-preview rounded-lg w-full"
                              v-on="on"
                            >
                              <div
                                v-if="item.pageId === 'blank'"
                                class="bordered border-light pa-2 body-2 font-weight-bold w-full text-center"
                              >
                                Not Printed / Blank
                              </div>
                              <img
                                v-else
                                :src="item.previewUrl"
                                class="page-preview"
                                style="max-height: 150px; max-width: 180px"
                              />
                            </div>
                          </template>
                        </v-select>
                      </div>
                      <v-btn
                        v-if="pageSelectionsChanged"
                        small
                        class="mt-3"
                        outlined
                        :loading="savingPageSelections"
                        @click="savePageSelections(job)"
                      >
                        Save Page Selections
                      </v-btn>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td>
                    <div class="d-flex align-center">
                      <v-select
                        v-model="job.statusId"
                        dense
                        outlined
                        hide-details
                        persistent-placeholder
                        placeholder="Select Status"
                        label="Status"
                        :items="statuses"
                        item-value="id"
                        item-text="name"
                        class="body-2 my-2"
                      />
                      <v-btn
                        small
                        text
                        class="ml-2"
                        :loading="saving"
                        @click="updateJobStatus(job)"
                      >
                        Save
                      </v-btn>
                    </div>
                  </td>
                </tr>
                <tr
                  class="hover-pointer"
                  @click="showSyncProject(job)"
                >
                  <td>
                    Project
                    <span
                      v-if="job.isPrintUpload"
                      class="font-weight-bold error--text"
                    >
                      (UPLOAD)
                    </span>
                  </td>
                  <td>
                    <span>Snapshot: {{ job.snapshotProjectId }}</span>
                    <span class="mx-2">|</span>
                    <span>User: {{ job.userProjectId }}</span>
                  </td>
                </tr>
                <tr>
                  <td>Print Files</td>
                  <td>
                    <div v-if="!job.files.length">No approved files</div>
                    <div
                      v-else
                      class="d-flex"
                    >
                      <v-btn
                        v-for="(file, j) in job.files"
                        :key="j"
                        small
                        outlined
                        :href="file.url"
                        class="mr-2"
                      >
                        {{ file.fileCode }}
                      </v-btn>
                      <v-btn
                        small
                        outlined
                        class="mr-2"
                        @click="simulate(job)"
                      >
                        Simulate
                      </v-btn>
                    </div>
                  </td>
                </tr>
                <template v-if="job.campaign">
                  <tr v-if="job.campaign">
                    <td>Addresses</td>
                    <td>
                      <div class="d-flex align-center justify-space-between">
                        <div v-if="job.campaign.validatedRecipientCount">
                          <div>
                            <span class="font-weight-bold">Original:</span>
                            {{ job.campaign.recipientCount | separator }}
                          </div>
                          <div>
                            <span class="font-weight-bold">Validated:</span>
                            {{ job.campaign.validatedRecipientCount | separator }}
                          </div>
                        </div>
                        <span v-else>
                          {{ job.campaign.recipientCount | separator }}
                        </span>

                        <v-btn
                          v-if="job.campaign.needsDownload"
                          :loading="downloading"
                          @click="purchaseAddresses"
                        >
                          Buy Lists
                        </v-btn>
                        <v-btn
                          v-else-if="job.campaign.externalRoutesNeeded"
                          :loading="downloading"
                          @click="addExternalRoutes"
                        >
                          Add External Routes
                        </v-btn>
                        <template v-else-if="job.campaign.isDirect">
                          <v-btn
                            v-if="job.campaign.addressFileUrl"
                            :loading="downloading"
                            :href="job.campaign.addressFileUrl"
                            target="_blank"
                          >
                            Orig.
                          </v-btn>
                          <v-btn
                            v-if="job.campaign.cleanedAddressFileUrl"
                            :loading="downloading"
                            :href="job.campaign.cleanedAddressFileUrl"
                            target="_blank"
                          >
                            Valid.
                          </v-btn>
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!job.campaign.needsDownload && job.campaign.isDirect">
                    <td></td>
                    <td>
                      <v-form
                        ref="validateForm"
                        v-model="validationValid"
                      >
                        <div class="d-flex align-center justify-space-between">
                          <v-select
                            v-model="job.campaign.validationType"
                            :items="validation_types"
                            label="Validation Type"
                            outlined
                            item-text="item2"
                            item-value="item1"
                            hide-details="auto"
                            dense
                          ></v-select>
                          <v-btn
                            class="ml-2"
                            :loading="downloading"
                            @click="validateAddresses"
                          >
                            Validate
                          </v-btn>
                        </div>
                      </v-form>
                    </td>
                  </tr>
                </template>

                <tr>
                  <td
                    colspan="2"
                    class="text-center black white--text"
                  >
                    ------ Product Details ------
                  </td>
                </tr>
                <tr>
                  <td>Product</td>
                  <td>
                    <router-link :to="`/print/products/${job.printProduct.id}`">
                      {{ job.printProduct.name }}
                    </router-link>
                  </td>
                </tr>
                <tr>
                  <td>Category</td>
                  <td>{{ job.categoryName }}</td>
                </tr>
                <tr>
                  <td>Fold</td>
                  <td>{{ job.fold }}</td>
                </tr>
                <tr>
                  <td>Stock</td>
                  <td>{{ job.stock }}</td>
                </tr>
                <tr>
                  <td>Finish</td>
                  <td>{{ job.finish }}</td>
                </tr>
                <tr>
                  <td>Color</td>
                  <td>{{ job.colorSpec }}</td>
                </tr>
                <tr>
                  <td>Quantity</td>
                  <td>{{ job.quantity }}</td>
                </tr>
                <tr>
                  <td>Options</td>
                  <td class="py-2">
                    <div
                      v-for="(option, i) in job.options"
                      :key="i"
                    >
                      <template v-if="option.productOption && option.productOption.option">
                        <span class="font-weight-bold">
                          {{ option.productOption.option.group.display }}:
                        </span>
                        {{ option.productOption.option.display }}
                      </template>
                      <span class="font-weight-bold">
                        {{ option.productOption.option.group.display }}:
                      </span>
                      {{ option.productOption.option.display }}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td
                    colspan="2"
                    class="text-center black white--text"
                  >
                    ------ Product Modifications ------
                  </td>
                </tr>

                <tr>
                  <td>Size</td>
                  <td class="py-2">
                    <div class="d-flex align-center">
                      <span>{{ job.coreProductName }}</span>
                      <v-btn
                        small
                        outlined
                        class="ml-3"
                        @click="changeSizeOpen = true"
                      >
                        Change size
                      </v-btn>
                    </div>
                    <v-alert
                      v-if="sizeChangedJobId === job.jobId"
                      type="warning"
                      text
                      dense
                      class="mt-2 mb-0 body-2"
                    >
                      Size changed. Open Edit Artwork to resize the design, then generate and approve
                      the print file.
                    </v-alert>
                  </td>
                </tr>

                <tr v-if="job.products.length">
                  <td>Product</td>
                  <td>
                    <div class="d-flex align-center pt-2">
                      <v-select
                        v-model="job.printQuantity.id"
                        dense
                        outlined
                        hide-details
                        persistent-placeholder
                        placeholder="Select Product"
                        label="New Product"
                        :items="job.products"
                        item-value="key"
                        item-text="value"
                        class="body-2 my-2"
                      />
                      <v-btn
                        small
                        text
                        class="ml-2"
                        :loading="saving"
                        @click="saveQuantity(job)"
                      >
                        Save
                      </v-btn>
                    </div>
                  </td>
                </tr>

                <tr v-if="job.colorSpecs.length">
                  <td>Color</td>
                  <td>
                    <div class="d-flex align-center">
                      <v-select
                        v-model="job.printQuantity.id"
                        dense
                        outlined
                        hide-details
                        persistent-placeholder
                        placeholder="Select Color"
                        label="New Color"
                        :items="job.colorSpecs"
                        item-value="key"
                        item-text="value"
                        class="body-2 my-2"
                      />
                      <v-btn
                        small
                        text
                        class="ml-2"
                        :loading="saving"
                        @click="saveQuantity(job)"
                      >
                        Save
                      </v-btn>
                    </div>
                  </td>
                </tr>

                <tr v-if="job.quantities.length">
                  <td>Quantity</td>
                  <td>
                    <div class="d-flex align-center">
                      <v-select
                        v-model="job.printQuantity.id"
                        dense
                        outlined
                        hide-details
                        persistent-placeholder
                        placeholder="Select Quantity"
                        label="New Quantity"
                        :items="job.quantities"
                        item-value="key"
                        item-text="value"
                        class="body-2 my-2"
                      />
                      <v-btn
                        small
                        text
                        class="ml-2"
                        :loading="saving"
                        @click="saveQuantity(job)"
                      >
                        Save
                      </v-btn>
                    </div>
                  </td>
                </tr>

                <tr v-if="job.availableOptionGroups.length">
                  <td>Options</td>
                  <td class="py-2">
                    <div
                      v-for="(item, i) in job.availableOptionGroups"
                      :key="i"
                      class="d-flex align-center"
                    >
                      <v-select
                        v-model="item.printProductOptionId"
                        dense
                        hide-details
                        outlined
                        persistent-placeholder
                        :placeholder="item.group.display"
                        :label="item.group.display"
                        :items="item.productOptions"
                        :rules="[$rules.required]"
                        item-text="option.display"
                        item-value="id"
                        class="body-2 my-2"
                      />
                      <v-btn
                        small
                        text
                        class="ml-2"
                        :color="item.orderLinePrintOptionId ? 'primary' : 'error'"
                        :loading="saving"
                        @click="
                          saveOption(job, item.orderLinePrintOptionId, item.printProductOptionId)
                        "
                      >
                        Save
                      </v-btn>
                    </div>
                  </td>
                </tr>

                <tr v-if="job.turnarounds.length">
                  <td>Turnaround</td>
                  <td>
                    <div class="d-flex align-center">
                      <v-select
                        v-model="job.printTurnaroundType.id"
                        dense
                        outlined
                        hide-details
                        persistent-placeholder
                        placeholder="Select Turnaround"
                        label="New Turnaround"
                        :items="job.turnarounds"
                        item-value="key"
                        item-text="value"
                        class="body-2 my-2"
                      />
                      <v-btn
                        small
                        text
                        class="ml-2"
                        :loading="saving"
                        @click="saveTurnaroundType(job)"
                      >
                        Save
                      </v-btn>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td
                    colspan="2"
                    class="text-center black white--text"
                  >
                    ------ Deliver ------
                  </td>
                </tr>
                <tr>
                  <td>Turnaround</td>
                  <td>{{ job.turnaroundDays }} day(s)</td>
                </tr>
                <tr>
                  <td>Submit By</td>
                  <td>{{ job.submitByDate | normalDateWithTime }}</td>
                </tr>
                <tr>
                  <td>Deliver By</td>
                  <td>
                    {{ job.earliestDeliveryDate | normalDate
                    }}<span v-if="job.latestDeliveryDate">
                      - {{ job.latestDeliveryDate | normalDate }}</span
                    >
                  </td>
                </tr>
                <tr>
                  <td>Ship Via</td>
                  <td>{{ job.shippingServiceName || 'Best Available' }}</td>
                </tr>
                <tr v-if="job.facilityCode">
                  <td>Ship From</td>
                  <td>{{ job.facilityCode }}</td>
                </tr>
                <tr v-if="job.address">
                  <td>Shipping To</td>
                  <td
                    class="py-2 hover-pointer"
                    @click="showEditAddress(job)"
                  >
                    <div>
                      {{ job.address.firstName }}
                      {{ job.address.lastName }}
                    </div>
                    <div v-if="job.address.company">
                      {{ job.address.company }}
                    </div>
                    <div>{{ job.address.line1 }}</div>
                    <div v-if="job.address.line2">
                      {{ job.address.line2 }}
                    </div>
                    <div>
                      {{ job.address.city }}, {{ job.address.state }}
                      {{ job.address.postalCode }}
                      {{ job.address.countryCode }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </div>
    <ChangeSize
      v-if="job.jobId"
      v-model="changeSizeOpen"
      :job="job"
      @changed="sizeChangedJobId = job.jobId"
    />
    <Portal :to="`drawerAppend-${drawerId}`">
      <div
        class="border-t pa-3"
        style="background-color: #edeaea"
      >
        <v-simple-table
          dense
          style="background-color: #edeaea"
        >
          <template #default>
            <tbody>
              <tr>
                <td>Price</td>
                <td>
                  {{ job.price | currency }}
                  <span
                    v-if="order.promoCode"
                    class="ml-3"
                  >
                    Promo: {{ order.promoCode }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>Cost (Est.)</td>
                <td>
                  {{ job.cost | currency }}
                  <span class="ml-3">
                    (Print: {{ job.baseCost | currency }}
                    <template v-if="job.turnaroundCost">
                      <span class="mx-1">|</span>
                      Turn: {{ job.turnaroundCost | currency }}
                    </template>
                    <template v-if="job.shippingCost">
                      <span class="mx-1">|</span>
                      Ship: {{ job.shippingCost | currency }}
                    </template>
                    <template v-if="job.listPurchaseCost">
                      <span class="mx-1">|</span>
                      List: {{ job.listPurchaseCost | currency }}
                    </template>
                    <template v-if="job.postageCost">
                      <span class="mx-1">|</span>
                      Postage: {{ job.postageCost | currency }}
                    </template>
                    <template v-if="job.salesTaxCost">
                      <span class="mx-1">|</span>
                      Taxes: {{ job.salesTaxCost | currency }} </template
                    >)
                  </span>
                </td>
              </tr>
              <tr v-if="job.actualCost">
                <td>Cost</td>
                <td>
                  {{ job.actualCost | currency }}
                  <span class="ml-3">
                    (Print: {{ job.actualBaseCost | currency }}
                    <template v-if="job.actualTurnaroundCost">
                      <span class="mx-1">|</span>
                      Turn: {{ job.actualTurnaroundCost | currency }}
                    </template>
                    <template v-if="job.actualShippingCost">
                      <span class="mx-1">|</span>
                      Ship: {{ job.actualShippingCost | currency }}
                    </template>
                    <template v-if="job.listPurchaseCost">
                      <span class="mx-1">|</span>
                      List: {{ job.listPurchaseCost | currency }}
                    </template>
                    <template v-if="job.actualPostageCost">
                      <span class="mx-1">|</span>
                      Postage: {{ job.actualPostageCost | currency }}
                    </template>
                    <template v-if="job.actualSalesTax">
                      <span class="mx-1">|</span>
                      Taxes: {{ job.actualSalesTax | currency }} </template
                    >)
                  </span>
                </td>
              </tr>
              <tr>
                <td>Profit (Est.)</td>
                <td class="font-weight-bold body-1">
                  <div>
                    <span class="success--text">
                      {{ job.profit | currency }}
                    </span>
                    |
                    <span class="font-weight-bold">
                      {{ job.estimatedMarkup | percentage }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="job.actualProfit">
                <td>Profit</td>
                <td class="font-weight-bold body-1">
                  <div>
                    <span class="success--text">
                      {{ job.actualProfit | currency }}
                    </span>
                    |
                    <span class="font-weight-bold">
                      {{ job.actualMarkup | percentage }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>Margin</td>
                <td class="font-weight-bold body-1">
                  <div>
                    {{ job.margin | percentage }}
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </Portal>
  </Drawer>
</template>

<script>
import Drawer from '@/components/layout/Drawer';
import ChangeSize from '@/components/print/job/ChangeSize';
import { cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import enums from '../../../helpers/enums';

export default {
  components: {
    Drawer,
    ChangeSize
  },

  data: () => ({
    orderId: 0,
    jobId: 0,
    tab: 'product',
    tabs: [
      {
        index: 0,
        name: 'Product'
      },
      {
        index: 1,
        name: 'Profit'
      },
      {
        index: 2,
        name: 'Prepare'
      }
    ],
    loading: true,
    saving: false,
    downloading: false,
    drawerId: enums.DRAWERS.PRINT_JOB_DETAIL,
    pageOptions: [],
    pageSelections: [],
    savingPageSelections: false,
    pageSelectionsChanged: false,
    validationValid: false,
    changeSizeOpen: false,
    sizeChangedJobId: 0
  }),

  computed: {
    drawerOpenedAt() {
      return this.$store.state.drawers.lastOpenedAt;
    },

    job() {
      return this.order.jobs?.find((x) => x.jobId === this.jobId) || {};
    },

    drawerTitle() {
      if (this.job.jobId) return 'Print Job # ' + this.job.jobId;
      return 'Print Job # ...';
    },

    ...mapGetters({
      order: 'orders/order',
      statuses: 'lookup/order_line_statuses',
      initialData: 'drawers/data',
      validation_types: 'lookup/validation_types'
    })
  },

  watch: {
    drawerOpenedAt(val, oldVal) {
      if (this.drawerId === this.$store.getters['drawers/topDrawer']?.id) {
        this.jobId = this.initialData(this.drawerId).jobId;
        this.orderId = this.initialData(this.drawerId).orderId;
        this.loadData();
      }
    }
  },

  methods: {
    loadData() {
      this.loading = true;
      this.$store
        .dispatch('orders/get', this.orderId)
        .then(() => {
          this.$store
            .dispatch('orders/getPrintablePages', this.job.snapshotProjectId)
            .then((printablePages) => {
              this.pageOptions = printablePages.pageOptions;
              this.pageSelections = this.job.pageSelections;
            });
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showSyncProject(job) {
      this.$store.commit('drawers/OPEN_DRAWER', enums.DRAWERS.PRINT_JOB_SYNC);
      this.$store.commit('drawers/SET_DATA', {
        drawerId: enums.DRAWERS.PRINT_JOB_SYNC,
        data: { job: job }
      });
    },
    showEditAddress(job) {
      this.$store.commit('drawers/OPEN_DRAWER', enums.DRAWERS.PRINT_JOB_EDIT_ADDRESS);
      this.$store.commit('drawers/SET_DATA', {
        drawerId: enums.DRAWERS.PRINT_JOB_EDIT_ADDRESS,
        data: { job: job }
      });
    },
    showJobValidate(job) {
      this.$store.commit('drawers/OPEN_DRAWER', enums.DRAWERS.PRINT_JOB_VALIDATE_COSTS);
      this.$store.commit('drawers/SET_DATA', {
        drawerId: enums.DRAWERS.PRINT_JOB_VALIDATE_COSTS,
        data: { job: job }
      });
    },
    saveQuantity(job) {
      if (!job.printQuantity.id) return;
      this.saving = true;
      Promise.all([
        this.$store.dispatch('orders/saveQuantity', {
          orderId: job.orderId,
          jobId: job.jobId,
          newQuantityId: job.printQuantity.id
        })
      ])
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    saveTurnaroundType(job) {
      if (!job.printTurnaroundType.id) return;
      this.saving = true;
      Promise.all([
        this.$store.dispatch('orders/saveTurnaroundType', {
          orderId: job.orderId,
          jobId: job.jobId,
          newTurnaroundTypeId: job.printTurnaroundType.id
        })
      ])
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    saveOption(job, optionLineId, productOptionId) {
      this.saving = true;
      Promise.all([
        this.$store.dispatch('orders/saveOption', {
          orderId: job.orderId,
          jobId: job.jobId,
          optionLineId: optionLineId,
          productOptionId: productOptionId
        })
      ])
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async prepareReprint(job) {
      if (
        await this.$root.$confirm(
          'Clear Data & Reprint',
          'This will detach and remove all previous job information. Are you sure you want to do this?',
          { color: 'error' }
        )
      ) {
        this.saving = true;
        Promise.all([
          this.$store.dispatch('orders/prepareReprint', {
            orderId: job.orderId,
            jobId: job.jobId,
            isPromo: false
          })
        ])
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    updateJobStatus(job) {
      this.saving = true;
      Promise.all([
        this.$store.dispatch('orders/updateJobStatus', {
          orderId: job.orderId,
          jobId: job.jobId,
          statusId: job.statusId,
          isPromo: false
        })
      ])
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    showAddExternalJob(job) {
      this.$store.commit('drawers/OPEN_DRAWER', enums.DRAWERS.PRINT_JOB_ADD_EXTERNAL_JOB);
      this.$store.commit('drawers/SET_DATA', {
        drawerId: enums.DRAWERS.PRINT_JOB_ADD_EXTERNAL_JOB,
        data: { job: job, isPromo: false }
      });
    },
    showAddTracking(job) {
      this.$store.commit('drawers/OPEN_DRAWER', enums.DRAWERS.PRINT_JOB_ADD_TRACKING);
      this.$store.commit('drawers/SET_DATA', {
        drawerId: enums.DRAWERS.PRINT_JOB_ADD_TRACKING,
        data: { job: job }
      });
    },
    pageSelected(selection, page) {
      var pages = cloneDeep(this.pageSelections);
      pages.forEach((x) => {
        if (x.number === page.number) {
          x.pageId = selection.pageId;
          x.previewUrl = selection.previewUrl;
        }
      });
      this.pageSelections = pages;
      this.pageSelectionsChanged = true;
    },
    savePageSelections() {
      // save the page seletions to the database
      var newPrintQuantityId = '';
      var colorSpec = null;
      var anyBlankPages = this.pageSelections.find((x) => x.pageId === 'blank');
      if (anyBlankPages && this.pageSelections.length === 2) {
        colorSpec = this.job.colorSpecs.find((x) => x.value.indexOf('4/0') > -1);
        if (colorSpec) newPrintQuantityId = colorSpec.key;
      } else if (!anyBlankPages && this.pageSelections.length >= 2) {
        colorSpec = this.job.colorSpecs.find((x) => x.value.indexOf('4/4') > -1);
        if (colorSpec && this.job.colorSpecId !== colorSpec.key) {
          newPrintQuantityId = colorSpec.key;
        }
      }
      this.savingPageSelections = true;
      this.$store
        .dispatch('orders/savePrintablePages', {
          orderId: this.order.id,
          jobId: this.job.jobId,
          pages: this.pageSelections,
          newPrintQuantityId: newPrintQuantityId
        })
        .then(() => {
          this.pageSelectionsChanged = false;
        })
        .finally(() => {
          this.savingPageSelections = false;
        });
    },
    simulate() {
      this.$http
        .post(`orders/getSubmitPayload?orderLineId=${this.jobId}&isPromo=false`)
        .then((response) => {
          var data = response.data;
          if (this.job.provider.toLowerCase() === 'catprint') {
            data = '<xmp>' + data + '</xmp>';
          }
          this.$root.$alert('Payload', data, {
            width: 800,
            cancelText: '',
            okText: 'Close'
          });
        });
    },
    purchaseAddresses() {
      this.downloading = true;
      this.$store
        .dispatch('orders/purchaseAddresses', { jobId: this.job.jobId, orderId: this.job.orderId })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.downloading = false;
        });
    },
    addExternalRoutes() {
      this.downloading = true;
      this.$store
        .dispatch('orders/addExternalRoutes', { jobId: this.job.jobId, orderId: this.job.orderId })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.downloading = false;
        });
    },
    validateAddresses() {
      if (this.$refs.validateForm.validate()) {
        this.downloading = true;
        this.$store
          .dispatch('orders/validateAddresses', {
            jobId: this.job.jobId,
            orderId: this.job.orderId,
            validationType: this.job.campaign.validationType
          })
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.downloading = false;
          });
      }
    }
  }
};
</script>

<style scoped>
.v-select {
  max-width: 200px !important;
  font-size: 13px;
}
.v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background: initial !important;
}
tr td:first-child {
  font-weight: 600;
  width: 110px;
}
.page-preview {
  border: solid 1.5px;
  border-radius: 5px;
  max-width: 100px;
  max-height: 30px;
}
</style>
